import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { loadCurrent } from 'app/state/utils/generation'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import {
  facilityScreenLoadFailed,
  getFacilityScreen,
  startLoadingFacilityScreen,
  stopLoadingFacilityScreen,
} from './actions'
import { FacilityScreenState } from './reducer'

type GetState = () => { facilityScreen: FacilityScreenState }

/** One request at a time: a strict-mode double mount, or a page effect firing during a load, joins it. */
let inFlight: Promise<unknown> | null = null

/**
 * The whole Facilities grid in one request: every facility across its four
 * states, each amendment in flight shown in place of the facility it amends,
 * deal and admin names, holds and the alert flags already on the row. The
 * backend reads six templates in one PQS statement plus four small lookups,
 * so the screen no longer loads the facility, amendment, deal and entity
 * lists — nor one lender-positions request per row — just to stitch a row.
 *
 * The response carries the store's generation at the time of the request; if
 * a write bumped it meanwhile the reducer drops the rows and this reads once
 * more (utils/generation.ts). On failure the store keeps what it had, the user
 * is told, and the loader still stops.
 */
export const loadFacilityScreen = () => (dispatch: Dispatch, getState?: GetState) => {
  if (inFlight) return inFlight
  const generation = () => getState?.().facilityScreen.generation ?? 0
  inFlight = loadCurrent(generation, g => load(g, dispatch)).finally(() => {
    // in flight is cleared BEFORE the loader stops: the grid's "reload once
    // while stale" effect fires on the loader stopping, and must start a new
    // read rather than join this finished one
    inFlight = null
    dispatch(stopLoadingFacilityScreen())
  })
  return inFlight
}

const load = async (generation: number, dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  try {
    dispatch(startLoadingFacilityScreen())
    const response = await client.get(`${apiServerUrl}/api/facility-management/screen`)
    return dispatch(getFacilityScreen(response.data, generation))
  } catch (e: any) {
    dispatch(facilityScreenLoadFailed())
    ErrorNotification({
      title: 'Could not load facilities',
      message: e?.response?.data?.error ?? 'The Facilities grid could not be loaded. Reload the page to try again.',
    })
  }
}
